import { useEffect, useState } from 'react';
import useSalesBotStore from '@/stores/SalesBotStore';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { saveAutopilotSettings } from '@/hooks/sales-bot';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';

const AutopilotTagging = () => {
  const [promptValue, setPromptValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [localTaggingEnabled, setLocalTaggingEnabled] = useState(false);

  const {
    taggingPrompt,
    taggingEnabled,
    updateTaggingPrompt,
    setTaggingEnabled,
  } = useSalesBotStore(
    useShallow((state) => ({
      taggingPrompt: state.taggingPrompt,
      taggingEnabled: state.taggingEnabled,
      updateTaggingPrompt: state.updateTaggingPrompt,
      setTaggingEnabled: state.setTaggingEnabled,
    }))
  );

  // Initialize tagging enabled from store
  useEffect(() => {
    setLocalTaggingEnabled(taggingEnabled);
  }, [taggingEnabled]);

  // Initialize prompt value from store when it's available
  useEffect(() => {
    if (taggingPrompt) {
      setPromptValue(taggingPrompt);
    }
  }, [taggingPrompt]);

  const handleToggleTagging = async (checked) => {
    try {
      setLocalTaggingEnabled(checked);

      // Build the payload conditionally
      const payload = {
        ai_tagging_enabled: checked,
      };

      // Only include prompt value if tagging is enabled and prompt exists
      if (checked && promptValue.trim() !== '') {
        payload.ai_tagging_prompt = promptValue;
      }

      await toast.promise(saveAutopilotSettings(payload), {
        loading: checked ? 'Enabling AI tagging...' : 'Disabling AI tagging...',
        success: () => {
          setTaggingEnabled(checked);
          if (checked && promptValue) {
            updateTaggingPrompt(promptValue);
          }
          return checked
            ? 'AI tagging enabled successfully'
            : 'AI tagging disabled successfully';
        },
        error: (error) => {
          // Revert the checkbox state on error
          setLocalTaggingEnabled(!checked);
          return (
            error.response?.data?.message ||
            'Failed to update AI tagging settings'
          );
        },
      });
    } catch (error) {
      // Revert the checkbox state on error
      setLocalTaggingEnabled(!checked);
      toast.error('Failed to update AI tagging settings');
    }
  };

  const handleSavePrompt = async () => {
    try {
      if (localTaggingEnabled && promptValue.trim() === '') {
        toast.error('Please enter an AI tagging prompt');
        return;
      }

      setIsSaving(true);

      // Build the payload conditionally
      const payload = {
        ai_tagging_enabled: localTaggingEnabled,
      };

      // Only include prompt value if tagging is enabled
      if (localTaggingEnabled) {
        payload.ai_tagging_prompt = promptValue;
      }

      await toast.promise(saveAutopilotSettings(payload), {
        loading: 'Saving settings...',
        success: () => {
          setTaggingEnabled(localTaggingEnabled);
          if (localTaggingEnabled) {
            updateTaggingPrompt(promptValue);
          }
          setIsSaving(false);
          return localTaggingEnabled
            ? 'AI tagging settings saved successfully'
            : 'AI tagging disabled successfully';
        },
        error: (error) => {
          setIsSaving(false);
          return error.response?.data?.message || 'Failed to save settings';
        },
      });
    } catch (error) {
      toast.error('Failed to save settings');
      setIsSaving(false);
    }
  };

  return (
    <div className='flex h-full flex-col overflow-hidden'>
      {/* AI Tagging Toggle Section */}
      <div className='flex-shrink-0 border-b p-6'>
        <div className='flex items-center justify-between'>
          <div className='flex-1'>
            <p className='text-sm text-muted-foreground'>
              Enable AI-powered automatic tagging to analyze conversations and
              apply appropriate tags to contacts based on the configured prompt
              below.
            </p>
          </div>
          <Checkbox
            id='enable-tagging'
            checked={localTaggingEnabled}
            onCheckedChange={handleToggleTagging}
            className='h-6 w-6'
          />
        </div>
      </div>

      <Separator />

      {/* Prompt Section */}
      <div className='flex min-h-0 flex-1 flex-col overflow-hidden p-6'>
        <div className='mb-4'>
          <h2 className='text-lg font-semibold'>AI Tagging Prompt</h2>
          <p className='text-sm text-muted-foreground'>
            Configure how the AI should analyze conversations and apply tags to
            contacts. Define your tagging rules, engagement levels, and tag
            management criteria.
          </p>
        </div>
        <div className='flex flex-1 flex-col'>
          <Textarea
            value={promptValue}
            onChange={(e) => setPromptValue(e.target.value)}
            placeholder='Enter your AI tagging prompt here...'
            disabled={!localTaggingEnabled}
            className='custom-scrollbar mb-4 h-[58vh] max-h-[100vh] resize-none'
          />
          <Button
            onClick={handleSavePrompt}
            disabled={isSaving || !localTaggingEnabled}
            className='w-64 self-end'
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AutopilotTagging;
